// src/constants/versionWatchers.ts
// ⚠️ 순환 참조 방지: @/stores 대신 동적 import 사용
// stores/index.ts → useAuthStore → watchCompanyRealtime → versionWatchers → @/stores 순환 방지
import type { Store } from 'pinia'
import type { Product } from '@farmfree/common/types/product/product'
import type { Category } from '@farmfree/common/types/category/category'
import type { Option } from '@farmfree/common/types/option/option'
import type { OptionGroup } from '@farmfree/common/types/option/optionGroup'
import type { CourierSaasAccount } from '@farmfree/common/types/courierSaas/courierSaasAccount'
import type { MarketplaceAccount } from '@farmfree/common/types/commerce/marketplaceAccount'
import type { Message } from '@farmfree/common/types/message/message'
import type { OrderPen } from '@farmfree/common/types/order/orderPen'
import type { SmsTemplate } from '@farmfree/common/types/template/smsTemplate'
import type { BankTransaction } from '@farmfree/common/types/bank/bankTransaction'
import type { Consignor } from '@farmfree/common/types/consignment/consignor'
import type { ConsignmentBatch } from '@farmfree/common/types/consignment/consignmentBatch'
import type { SenderPreset } from '@farmfree/common/types/consignment/senderPreset'
import type { PaymentMatchGroup } from '@farmfree/common/types/payment/paymentMatchGroup'

/**
 * 버전 키 목록
 */
export const versionKeys = [
  'productVersion',
  'categoryVersion',
  'optionVersion',
  'optionGroupVersion',
  'courierSaasVersion',
  'marketplaceVersion',
  'messageVersion',
  'contactVersion',
  'orderPenVersion',
  'orderPenArchiveVersion',
  'smsTemplateVersion',
  'bankSettingsVersion',
  'bankTransactionVersion',
  'manualInputVersion',
  'consignorVersion',
  'consignmentBatchVersion',
  'senderPresetVersion',
  'paymentMatchGroupVersion',
] as const

/**
 * 버전 키 타입 (자동 추론)
 */
export type VersionKey = (typeof versionKeys)[number]

// Store 타입 정의 (실용적인 접근 - 필수 메서드만 타입 체크)
// 각 Store는 구조가 다를 수 있으므로, 최소한의 공통 인터페이스만 정의
type BaseStore = Store<string, any, any, any> & {
  items: any[]
}

type StoreMap = {
  productVersion: BaseStore & { items: Product[] }
  categoryVersion: BaseStore & { items: Category[] }
  optionVersion: BaseStore & { items: Option[] }
  optionGroupVersion: BaseStore & { items: OptionGroup[] }
  courierSaasVersion: BaseStore & { items: CourierSaasAccount[] }
  marketplaceVersion: BaseStore & { items: MarketplaceAccount[] }
  messageVersion: BaseStore & { items: Message[] }
  contactVersion: BaseStore // Contact Store는 특수 구조
  orderPenVersion: BaseStore // OrderPen Store도 특수 구조
  orderPenArchiveVersion: BaseStore
  smsTemplateVersion: BaseStore & { items: SmsTemplate[] }
  bankSettingsVersion: BaseStore
  bankTransactionVersion: BaseStore & { items: BankTransaction[] }
  manualInputVersion: BaseStore
  consignorVersion: BaseStore & { items: Consignor[] }
  consignmentBatchVersion: BaseStore & { items: ConsignmentBatch[] }
  senderPresetVersion: BaseStore & { items: SenderPreset[] }
  paymentMatchGroupVersion: BaseStore & { matchGroups: PaymentMatchGroup[] }
}

export type VersionWatcherMap = {
  [K in VersionKey]: {
    label: string
    store: () => Promise<StoreMap[K]> // async 함수로 변경
  }
}

// 순환 참조 방지를 위해 동적 import 패턴 사용
// store 함수가 호출될 때 import하여 순환 참조 회피
export const versionWatchers: VersionWatcherMap = {
  productVersion: {
    label: '상품',
    store: async () => {
      const { useProductStore } = await import('@/stores')
      return useProductStore()
    },
  },
  categoryVersion: {
    label: '카테고리',
    store: async () => {
      const { useCategoryStore } = await import('@/stores')
      return useCategoryStore()
    },
  },
  optionVersion: {
    label: '옵션',
    store: async () => {
      const { useOptionStore } = await import('@/stores')
      return useOptionStore()
    },
  },
  optionGroupVersion: {
    label: '옵션 그룹',
    store: async () => {
      const { useOptionGroupStore } = await import('@/stores')
      return useOptionGroupStore()
    },
  },
  courierSaasVersion: {
    label: '택배사 SaaS',
    store: async () => {
      const { useCourierSaasStore } = await import('@/stores')
      return useCourierSaasStore()
    },
  },
  marketplaceVersion: {
    label: '마켓플레이스',
    store: async () => {
      const { useMarketplaceStore } = await import('@/stores')
      return useMarketplaceStore()
    },
  },
  messageVersion: {
    label: '메시지',
    store: async () => {
      const { useMessageStore } = await import('@/stores')
      return useMessageStore()
    },
  },
  contactVersion: {
    label: '연락처',
    store: async () => {
      const { useContactStore } = await import('@/stores')
      return useContactStore()
    },
  },
  orderPenVersion: {
    label: '활성 주문',
    store: async () => {
      const { useActiveOrderStore } = await import('@/stores')
      return useActiveOrderStore()
    },
  },
  orderPenArchiveVersion: {
    label: '아카이브 주문',
    store: async () => {
      const { useArchivedOrderStore } = await import('@/stores')
      return useArchivedOrderStore()
    },
  },
  smsTemplateVersion: {
    label: 'SMS 템플릿',
    store: async () => {
      const { useSmsTemplateStore } = await import('@/stores')
      return useSmsTemplateStore()
    },
  },
  bankSettingsVersion: {
    label: '은행 설정',
    store: async () => {
      const { useBankSettingsStore } = await import('@/stores')
      return useBankSettingsStore()
    },
  },
  bankTransactionVersion: {
    label: '은행 거래내역',
    store: async () => {
      const { useBankTransactionStore } = await import('@/stores')
      return useBankTransactionStore()
    },
  },
  manualInputVersion: {
    label: '수동입력',
    store: async () => {
      const { useManualInputStore } = await import('@/stores')
      return useManualInputStore()
    },
  },
  consignorVersion: {
    label: '위탁파트너',
    store: async () => {
      const { useConsignorStore } = await import('@/stores')
      return useConsignorStore()
    },
  },
  consignmentBatchVersion: {
    label: '위탁배치',
    store: async () => {
      const { useConsignmentBatchStore } = await import('@/stores')
      return useConsignmentBatchStore()
    },
  },
  senderPresetVersion: {
    label: '발송인 프리셋',
    store: async () => {
      const { useSenderPresetStore } = await import('@/stores')
      return useSenderPresetStore()
    },
  },
  paymentMatchGroupVersion: {
    label: '결제 매칭',
    store: async () => {
      const { usePaymentMatchStore } = await import('@/stores/payment/usePaymentMatchStore')
      return usePaymentMatchStore()
    },
  },
}
